import React, { useCallback } from 'react';
import config from '@config/index';
import { useVendor } from '@context/VendorContext';
import { useEventLeadsStatus } from '@features/events/hooks/event-leads/useEventLeadsStatus';
import { useSearchEventLeads } from '@features/events/hooks/event-leads/useSearchEventLeads';
import { FlexView, Icon, Loading, Text } from '@goodfynd/ui';
import { Link } from '@goodfynd/ui';
import { useOrganizationHasFeatureAccess } from '@hooks/useFeatureAccess';
import { getDashboardRoutePath } from '@utils/routes-util';

import routes from '../../../config/routes';
import { OverviewCard } from '../overview-card';

export default function FyndAIEventsOverview() {
  const { vendor } = useVendor();

  const hasEventLeadsAccess = useOrganizationHasFeatureAccess(
    config.features.eventLeads,
    config.features.eventLeadsLegacy
  );

  const { data: statusData, isLoading: isStatusLoading } = useEventLeadsStatus(
    vendor.organizationId,
    { enabled: hasEventLeadsAccess }
  );

  const { total, isLoading: isSearchLoading } = useSearchEventLeads(
    vendor.organizationId,
    { page: 1, rows: 1 },
    { enabled: hasEventLeadsAccess && !statusData?.isRunning }
  );

  const isLoading = isStatusLoading || isSearchLoading;
  const isGenerating = statusData?.isRunning ?? false;
  const processingMinutes = statusData?.minutes ?? 0;

  const renderLoading = useCallback(
    () => (
      <FlexView align="center" height="100%" justify="center">
        <Loading type="circle" />
      </FlexView>
    ),
    []
  );

  const renderGenerating = useCallback(
    () => (
      <FlexView
        align="center"
        direction="column"
        gap={16}
        height="100%"
        justify="center"
      >
        <Loading size={32} type="circle" />
        <FlexView align="center" direction="column" gap={4}>
          <Text as="subhead2">Generating Event Leads</Text>
          <Text as="body2" color="TextIconSecondary">
            Running for {processingMinutes} minute
            {processingMinutes !== 1 ? 's' : ''}
          </Text>
        </FlexView>
      </FlexView>
    ),
    [processingMinutes]
  );

  const renderResults = useCallback(
    () => (
      <FlexView
        align="center"
        direction="column"
        gap={16}
        height="100%"
        justify="center"
      >
        <FlexView align="center" direction="column" gap={8}>
          <Icon color="TextIconPrimary" name="events_ai" size={24} />
          <Text as="heading2">{total ?? 0}</Text>
        </FlexView>
        <Text as="body2" color="TextIconSecondary">
          event leads found
        </Text>
        <Link
          href={getDashboardRoutePath(
            routes.dashboard.events.leads,
            vendor.nameId
          )}
        >
          <Text
            as="subhead3"
            inherit
            style={{ display: 'flex', alignItems: 'center' }}
            tag="span"
          >
            Browse Leads <Icon name="chevron_right" size={16} />
          </Text>
        </Link>
      </FlexView>
    ),
    [total, vendor.nameId]
  );

  const renderContent = useCallback(() => {
    if (isLoading) {
      return renderLoading();
    }

    if (isGenerating) {
      return renderGenerating();
    }

    return renderResults();
  }, [isLoading, isGenerating, renderLoading, renderGenerating, renderResults]);

  if (!hasEventLeadsAccess) {
    return null;
  }

  return (
    <OverviewCard
      id="overview-fyndai-events"
      showDateFilter={false}
      title="FyndAI Events"
      titleRight={
        <Link
          href={getDashboardRoutePath(
            routes.dashboard.events.leads,
            vendor.nameId
          )}
        >
          <Text as="subhead3" inherit>
            View All
          </Text>
        </Link>
      }
    >
      {renderContent()}
    </OverviewCard>
  );
}
